import { baseApiAuthenticated } from '@/api/v2/index';
import { urlBuilder } from '@/api/utils';
import { SourceConfiguration } from '@/models/bulkUpdate';
import {
    BackendProductConfiguration,
    ProductConfiguration,
    transformProductConfigurationFromResponse,
    transformProductConfigurationToBackend,
} from '@/models/product';

export const CACHE_KEY_BULK_UPDATES = 'PRODUCT_CONFIGURATIONS_BULK_UPDATES';

type Configuration = {
    keycard: boolean;
    barcode: boolean;
    pickup: boolean;
    internalProduction: boolean;
    mobileFlow?: boolean;
};

const productConfigurationUrls = (liftOperatorId?: string) => ({
    productConfigurations: urlBuilder('liftoperators', 'productconfigurations'),
    bulkUpdates: urlBuilder(
        'liftoperators',
        `productconfigurations/${liftOperatorId}/bulk-update`
    ),
});

export const productConfigurationsApi = baseApiAuthenticated
    .enhanceEndpoints({
        addTagTypes: ['PRODUCT_CONFIGURATION'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getProductConfigurations: builder.query<
                ProductConfiguration[],
                { liftOperatorId: string }
            >({
                query: ({ liftOperatorId }) =>
                    `${
                        productConfigurationUrls().productConfigurations
                    }/${liftOperatorId}`,

                transformResponse: (response: {
                    productConfigurations: BackendProductConfiguration[];
                }) => {
                    return response.productConfigurations.map(
                        transformProductConfigurationFromResponse
                    );
                },

                providesTags: ['PRODUCT_CONFIGURATION'],
            }),

            getProductConfiguration: builder.query<
                ProductConfiguration,
                { liftOperatorId: string; productId: string }
            >({
                query: ({ liftOperatorId }) =>
                    `${
                        productConfigurationUrls().productConfigurations
                    }/${liftOperatorId}`,

                transformResponse: (
                    response: {
                        productConfigurations: BackendProductConfiguration[];
                    },
                    _meta,
                    arg
                ) => {
                    return response.productConfigurations
                        .map(transformProductConfigurationFromResponse)
                        .find(
                            (productConfiguration) =>
                                productConfiguration.productId === arg.productId
                        );
                },

                providesTags: ['PRODUCT_CONFIGURATION'],
            }),
            postBulkUpdates: builder.mutation<
                boolean,
                {
                    liftOperatorId: string;
                    productIds: string[];
                    sourceProduct?: SourceConfiguration;
                    removePriceConditions?: boolean;
                    removeContingentConditions?: boolean;
                    enabled?: boolean;
                    portalConfiguration?: Configuration;
                }
            >({
                query: ({ liftOperatorId, ...rest }) => ({
                    url: productConfigurationUrls(liftOperatorId).bulkUpdates,
                    method: 'POST',
                    body: {
                        liftOperatorId,
                        ...rest,
                    },
                }),

                invalidatesTags: ['PRODUCT_CONFIGURATION'],
            }),

            putProductConfiguration: builder.mutation<
                ProductConfiguration,
                {
                    liftOperatorId: string;
                    productConfiguration: ProductConfiguration;
                }
            >({
                query: ({ liftOperatorId, productConfiguration }) => ({
                    url: `${
                        productConfigurationUrls().productConfigurations
                    }/${liftOperatorId}/${productConfiguration.id}`,
                    method: 'PUT',
                    body: transformProductConfigurationToBackend(
                        productConfiguration
                    ),
                }),

                transformResponse: (response: BackendProductConfiguration) => {
                    return transformProductConfigurationFromResponse(response);
                },

                invalidatesTags: ['PRODUCT_CONFIGURATION'],
            }),

            postProductConfiguration: builder.mutation<
                ProductConfiguration,
                {
                    liftOperatorId: string;
                    productConfiguration: ProductConfiguration;
                }
            >({
                query: ({ liftOperatorId, productConfiguration }) => ({
                    url: `${
                        productConfigurationUrls().productConfigurations
                    }/${liftOperatorId}`,
                    method: 'POST',
                    body: transformProductConfigurationToBackend(
                        productConfiguration
                    ),
                }),

                transformResponse: (response: BackendProductConfiguration) => {
                    return transformProductConfigurationFromResponse(response);
                },

                invalidatesTags: ['PRODUCT_CONFIGURATION'],
            }),
        }),
    });

export const {
    usePostBulkUpdatesMutation,
    useGetProductConfigurationsQuery,
    useGetProductConfigurationQuery,
    usePostProductConfigurationMutation,
    usePutProductConfigurationMutation,
} = productConfigurationsApi;

type UsePostOrPutConfigurationMutation = {
    liftOperatorId: string;
    productConfiguration: ProductConfiguration;
};

export const usePostOrPutConfigurationMutation = (
    params: UsePostOrPutConfigurationMutation
): ReturnType<
    | typeof usePostProductConfigurationMutation
    | typeof usePutProductConfigurationMutation
> => {
    const [postProductConfiguration, postOptions] =
        usePostProductConfigurationMutation();
    const [putProductConfiguration, putOptions] =
        usePutProductConfigurationMutation();

    return !params.productConfiguration.id
        ? [postProductConfiguration, postOptions]
        : [putProductConfiguration, putOptions];
};
